import React, { useEffect, useState } from 'react';

import type { UploadStage } from '@/types/poc/helia';

const COMPLETE_FADE_DELAY_MS = 1500;
const COMPLETE_REMOVE_DELAY_MS = 2200;
const PROGRESS_MIN = 0;
const PROGRESS_MAX = 100;
const MAX_RETRY_COUNT = 3;

const STAGE_LABELS: Record<UploadStage, string> = {
  encrypting: 'Encrypting file...',
  uploading: 'Storing locally...',
  announcing: 'Announcing to network...',
  'hap-downloading': 'HAP downloading file...',
  'hap-registering': 'Registering with backend...',
  complete: 'Upload complete',
  error: 'Upload failed',
};

export interface FileUploadProgressProps {
  uploadId: string;
  fileName: string;
  progress: number;
  stage: UploadStage;
  error?: string;
  retryCount?: number;
  variant?: 'card' | 'embedded';
  onRetry?: (uploadId: string) => void;
  onComplete?: (uploadId: string) => void;
}

export function FileUploadProgress({
  uploadId,
  fileName,
  progress,
  stage,
  error,
  retryCount = 0,
  variant = 'card',
  onRetry,
  onComplete,
}: FileUploadProgressProps): React.ReactElement {
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    if (stage !== 'complete') {
      setIsFading(false);
      return undefined;
    }

    const fadeTimer = setTimeout(() => {
      setIsFading(true);
    }, COMPLETE_FADE_DELAY_MS);

    const removeTimer = setTimeout(() => {
      onComplete?.(uploadId);
    }, COMPLETE_REMOVE_DELAY_MS);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, [onComplete, stage, uploadId]);

  const resolvedProgress = Math.max(PROGRESS_MIN, Math.min(PROGRESS_MAX, progress));
  const canRetry = stage === 'error' && retryCount < MAX_RETRY_COUNT;
  const containerClassName = variant === 'embedded'
    ? 'transition-opacity duration-700'
    : 'rounded-lg border border-border bg-background p-4 transition-opacity duration-700';

  return (
    <div
      className={[
        containerClassName,
        isFading ? 'opacity-0' : 'opacity-100',
      ].join(' ')}
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{fileName}</p>
          <p className="text-xs text-muted-foreground">{STAGE_LABELS[stage]}</p>
        </div>
        <span className="text-sm font-medium text-foreground">{Math.round(resolvedProgress)}%</span>
      </div>

      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div
          className={[
            'h-full rounded-full transition-[width] duration-300',
            stage === 'error'
              ? 'bg-destructive'
              : stage === 'complete'
                ? 'bg-primary'
                : 'bg-primary',
          ].join(' ')}
          style={{ width: `${resolvedProgress}%` }}
        />
      </div>

      {(stage === 'error' || canRetry) && (
        <div className="mt-3 flex items-start justify-between gap-3">
          <p className="text-sm text-destructive">
            {error ?? 'Upload failed.'}
          </p>
          {canRetry && (
            <button
              type="button"
              className="shrink-0 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
              onClick={() => {
                onRetry?.(uploadId);
              }}
            >
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default FileUploadProgress;
